import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import {
  CONSENT_OPEN_EVENT,
  denyConsent,
  getConsent,
  grantConsent,
  hasTrackingTags,
  isConsentRequired,
  isTrackedPath,
} from '../utils/tracking';

// O botão flutuante do WhatsApp lê esta variável para subir acima do banner.
const OFFSET_VAR = '--cookie-banner-offset';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Banner = styled.section`
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  width: min(880px, calc(100% - 32px));
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1rem 1.25rem;
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.cardForeground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.overlay};
  font-family: ${({ theme }) => theme.fonts.primary};
  animation: ${slideUp} 0.3s ease-out;

  @media (max-width: 640px) {
    left: 0;
    bottom: 0;
    transform: none;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 0.85rem;
    border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
    padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom, 0px));
  }
`;

const Text = styled.p`
  flex: 1;
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.neutral[700]};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-shrink: 0;

  @media (max-width: 640px) {
    & > button {
      flex: 1;
    }
  }
`;

// Mesmo estilo nos dois botões: recusar não pode ser mais difícil que aceitar.
const ChoiceButton = styled.button`
  min-width: 112px;
  padding: 0.65rem 1.1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  font: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ring};
    outline-offset: 2px;
  }
`;

const useBannerOffset = (ref: React.RefObject<HTMLElement | null>, active: boolean) => {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const element = ref.current;
    if (!active || !element) return undefined;

    const update = () => {
      const { bottom } = getComputedStyle(element);
      const gap = Number.parseFloat(bottom) || 0;
      root.style.setProperty(OFFSET_VAR, `${element.offsetHeight + gap}px`);
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(element);

    return () => {
      observer?.disconnect();
      root.style.removeProperty(OFFSET_VAR);
    };
  }, [ref, active]);
};

const CookieConsent: React.FC = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => getConsent() === null);
  const bannerRef = useRef<HTMLElement>(null);
  const visible = open && isTrackedPath(pathname) && hasTrackingTags() && isConsentRequired();

  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(CONSENT_OPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, reopen);
  }, []);

  useBannerOffset(bannerRef, visible);

  if (!visible) return null;

  const accept = () => {
    grantConsent();
    setOpen(false);
  };

  const decline = () => {
    denyConsent();
    setOpen(false);
  };

  return (
    <Banner ref={bannerRef} aria-label="Aviso de cookies">
      <Text>
        Usamos cookies para medir visitas e mostrar anúncios mais relevantes (Google e Meta).
        O site funciona normalmente se você recusar. Saiba mais na{' '}
        <Link to="/politica-de-privacidade">Política de Privacidade</Link>.
      </Text>
      <Actions>
        <ChoiceButton type="button" onClick={decline}>
          Recusar
        </ChoiceButton>
        <ChoiceButton type="button" onClick={accept}>
          Aceitar
        </ChoiceButton>
      </Actions>
    </Banner>
  );
};

export default CookieConsent;
